import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from "@angular/animations";
import { Router } from '@angular/router';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';
import { SignalsService } from '@shared/services/signals/signals.service';
import { AppService } from 'app/app.service';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FiledownloadService } from './service/filedownload.service';
import { catchError, from, Observable, of, Subject, Subscription, switchMap, takeUntil, tap } from 'rxjs';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';
import { DateTime } from 'luxon';
import { MessageService } from 'primeng/api';
import { environment } from '@environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { DownloadBlob } from './download/model/downloadBlob';
import { ColorPickerModule } from 'primeng/colorpicker';
import { GoogleEarthService } from './service/google-earth.service';

interface CheckboxItem {
  id: number;
  name: string;
  formName: string;
}

@Component({
    selector: 'app-export',
    imports: [
        CommonModule,
        AccordionModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        DatePickerModule,
        ColorPickerModule
    ],
    templateUrl: './export.component.html',
    styleUrl: './export.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger("fadeOpenClose", [
            state("closed", style({
                height: 0,
                opacity: 0,
                overflow: "hidden",
                paddingTop: 0
            })),
            state("open", style({
                height: "*",
                opacity: 1,
                overflow: "hidden",
                paddingTop: "0.5rem"
            })),
            transition("closed <=> open", animate("300ms ease-in-out")),
        ])
    ]
})

export class ExportComponent implements OnInit, OnDestroy {
  private readonly _baseUrl = environment.apiUrl;
  theme$ = this.appService.theme$;
  form: FormGroup = new FormGroup('');
  employeeNumber?: string;
  checkboxItems: CheckboxItem[] = [];
  calMaxDate: Date = new Date();
  selectedCheckboxId: number | null = null;
  destroy$ = new Subject<void>();
  activeIndex: string = '100'; // Default index that doesn't exist, undefined not acceptable
  fileReader: FileReader | undefined;

  //Career Map Projection properties
  gLineRouteColorDefaultCB: boolean = true;
  gLineRouteColorCB: boolean = false;
  gLineSelectColorCB: boolean = false;
  gLineSelectedColor: string = '#1c80cf';

  //Google Earth properties
  gxLineRouteColorDefaultCB: boolean = true;
  gxLineSelectColorCB: boolean = false;
  gxLineSelectedColor: string = '#1c80cf';
  gxLabelMarkersToggle: boolean = false;
  gxIconMarkersToggle: boolean = false;

  constructor(
    public appService: AppService,
    private fb: FormBuilder,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private signalService: SignalsService,
    private downloadService: FiledownloadService,
    private errorLogService: ErrorLogService,
    private messageService: MessageService,
    private googleEarthService: GoogleEarthService
  ) { }

  ngOnInit(): void {
    this.downloadService.initDB();
    this.breadcrumbService.removeBreadcrumb(['Downloads']);
    this.gLineRouteColorDefaultCB = true;
    const activeIndex = sessionStorage.getItem('export_activeIndex');
    if (activeIndex) {
      this.activeIndex = activeIndex;
    } else {
      this.activeIndex = '100';
    }

    this.checkboxItems = [
      { id: 0, name: 'All', formName: 'allCheckbox' },
      { id: 1, name: 'Current Month', formName: 'currentMonthCheckbox' },
      { id: 2, name: 'Current Year', formName: 'currentYearCheckbox' },
      { id: 3, name: 'Last 6 Months', formName: 'lastSixMonthsCheckbox' },
      { id: 4, name: 'Last 12 Months', formName: 'lastTwelveMonthsCheckbox' },
      { id: 5, name: 'Date Range', formName: 'dateRangeCheckBox' },
      { id: 6, name: 'Effective Period', formName: 'effectivePeriodCheckBox' },
    ];

    // *? If planning to save selections from form recall here.  In analysis...
    //const activeForm = sessionStorage.getItem('form') ? sessionStorage.getItem('form') : undefined;

    const controlsGroup = this.checkboxItems.reduce((acc, item) => {
      acc[item.formName] = new FormControl(false);
      return acc;
    }, {} as { [key: string]: FormControl });

    // Add date controls (not checkboxes) to the form group
    controlsGroup['dateRange'] = new FormControl<Date | null>(null);
    controlsGroup['effectivePeriod'] = new FormControl<Date | null>(null);


    this.form = this.fb.group(controlsGroup, {
      validators: [
        this.validateCheckboxes(),
        this.dateRangeValidator(),
        this.dateRangeCheckBoxValidator(),
        this.effectivePeriodValidator(),
        this.effectivePeriodCheckBoxValidator()
      ]
    });
  }

  validateCheckboxes(): ValidatorFn {
    return (formGroup: AbstractControl) => {
      const controls = (formGroup as FormGroup).controls;
      const isAnyChecked = Object.entries(controls)
        .filter(([key]) => key !== 'dateRangeCheckBox' && key !== 'effectivePeriodCheckBox')
        .some(([, control]) => control.value);
      return isAnyChecked ? null : { atLeastOneRequired: true };
    };
  }

  dateRangeCheckBoxValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const dateRangeCheckBox: boolean = control.get('dateRangeCheckBox')?.value;
      const dateRange: Date[] = control.get('dateRange')?.value;
      if (dateRangeCheckBox && (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1])) {
        return { 'dateRangeCheckBoxError': true };
      } else {
        return null;
      }
    }
  }

  dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const dateRange: Date[] = control.get('dateRange')?.value;
      if (dateRange && (dateRange[1] === null)) {
        return { 'dateRangeError': true };
      } else {
        return null;
      }
    }
  }

  effectivePeriodCheckBoxValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const effectivePeriodCheckBox: boolean = control.get('effectivePeriodCheckBox')?.value;
      const effectivePeriod: Date = control.get('effectivePeriod')?.value;
      if (effectivePeriodCheckBox && !effectivePeriod) {
        return { 'effectivePeriodCheckBoxError': true };
      } else {
        return null;
      }
    }
  }

  effectivePeriodValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const effectivePeriod: Date = control.get('effectivePeriod')?.value;
      if (effectivePeriod && effectivePeriod === null) {
        return { 'effectivePeriodError': true };
      } else {
        return null;
      }
    }
  }

  dateRangeValidationError() {
    return (this.form.errors?.dateRangeError && this.form.dirty) ?? false;
  }

  effectivePeriodValidationError() {
    return (this.form.errors?.effectivePeriodError && this.form.dirty) ?? false;
  }

  onCheckboxChange(id: number, event: any): void {
    if (this.selectedCheckboxId !== null && this.selectedCheckboxId !== id) {
      const prevItem = this.checkboxItems.find(item => item.id === this.selectedCheckboxId);
      if (prevItem) {
        this.form.get(prevItem.formName)?.setValue(false);
      }
    }
    this.form.get(this.checkboxItems[id].formName)?.setValue(event.target.checked);

    this.selectedCheckboxId = this.selectedCheckboxId === id ? null : id;
    if ((id != 5 && id != 6) || id === null) {
      this.form.get('dateRangeCheckBox')?.setValue(null);
      this.form.get('effectivePeriodCheckBox')?.setValue(null);
      this.form.get('dateRange')?.setValue(null);
      this.form.get('effectivePeriod')?.setValue(null);
      //this.form.reset();
    }
    else if (id != 6 && id == 5) {
      this.form.get('effectivePeriodCheckBox')?.setValue(null);
      this.form.get('effectivePeriod')?.setValue(null);
    }
    else if (id === 6) {
      this.form.get('dateRangeCheckBox')?.setValue(null);
      this.form.get('dateRange')?.setValue(null);
    }

  }

  onMapProjRouteColorChangeCB(event: any) {
    if (event.target.checked) {
      this.gLineRouteColorCB = true;
      this.gLineRouteColorDefaultCB = false;
      this.gLineSelectColorCB = false;
      this.gLineSelectedColor = '#1c80cf'; // Reset to default color
    }
  }

  onMapProjRouteColorDefaultCB(event: any) {
    if (event.target.checked) {
      this.gLineRouteColorCB = false;
      this.gLineRouteColorDefaultCB = true;
      this.gLineSelectColorCB = false;
      this.gLineSelectedColor = '#1c80cf'; // Reset to default color
    }
  }

  onMapProjSelectColorCB(event: any) {
    if (event.target.checked) {
      this.gLineSelectColorCB = true;
      this.gLineRouteColorCB = false;
      this.gLineRouteColorDefaultCB = false;
    }
  }

  onGxMapRouteColorDefaultCB(event: any) {
    if (event.target.checked) {
      this.gxLineSelectColorCB = false;
      this.gxLineRouteColorDefaultCB = true;
      this.gxLineSelectedColor = '#1c80cf'; // Reset to default color
    }
  }

  onGxMapSelectColorCB(event: any) {
    if (event.target.checked) {
      this.gxLineSelectColorCB = true;
      this.gxLineRouteColorDefaultCB = false;
      this.gxLineSelectedColor = '#1c80cf'; // Reset to default color
    }
  }

  isChecked(id: number): boolean {
    return this.selectedCheckboxId === id;
  }

  onCellPress(cell: string): void {
    if (cell === 'download') {
      this.breadcrumbService.addToBreadcrumbs('/export', 'Export');
      if (this.activeIndex != undefined || this.activeIndex !== null) {
        sessionStorage.setItem('export_activeIndex', this.activeIndex?.toString() || '0');
      }
      this.router.navigate(['/export/downloads']);
    }
  }

  trackById(index: number, item: any): number {
    return item.id;
  }

  onSubmitGetCSV(): void {
    this.signalService.isLoading$.set(true);
    this.fetchFileCSV().pipe(
      takeUntil(this.destroy$),
      tap((blob) => {
        if (!blob) {
          console.error('Blob is undefined or null');
          this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: 
            OnSubmit - Blob is undefined or null`);
          return;
        }
        this.signalService.isLoading$.set(false);
        this.breadcrumbService.addToBreadcrumbs('/export', 'Export');
        sessionStorage.setItem('export_activeIndex', this.activeIndex?.toString() || '0');
        this.router.navigate(['/export/downloads']);
      }),

    ).subscribe();
  }

  onSubmitGetMapProj(): void {
    this.signalService.isLoading$.set(true);
    this.fetchMapProj().pipe(
      takeUntil(this.destroy$),
      tap((blob) => {
        if (!blob) {
          console.error('Blob is undefined or null');
          this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: 
            OnSubmit - Blob is undefined or null`);
          return;
        }
        this.signalService.isLoading$.set(false);
        const gLineRouteColor = this.gLineRouteColorCB ? 'true' : 'false';
        this.breadcrumbService.addToBreadcrumbs('/export', 'Export');
        sessionStorage.setItem('export_activeIndex', this.activeIndex?.toString() || '1');

        this.router.navigate(['/export/mapproj', gLineRouteColor, this.gLineSelectedColor]);
      }),
    ).subscribe();
  }

  toggleMarkers(event: any): void {
    this.gxLabelMarkersToggle = event.target.checked;
  }
  toggleIconMarkers(event: any): void {
    this.gxIconMarkersToggle = event.target.checked;
  }


  /**
   * Handles the submission process for exporting logbook data to a Google Earth KML file.
   *
   * This method performs the following steps:
   * 1. Sets the loading state to true.
   * 2. Fetches the map projection data as a Blob.
   * 3. Validates the Blob and logs an error if it is invalid.
   * 4. Retrieves the employee number from the signal service.
   * 5. Reads the Blob as text and parses it as JSON.
   * 6. Generates KML data from the parsed JSON using the Google Earth service.
   * 7. Creates a new KML Blob and saves it using the download service.
   * 8. Handles errors in JSON parsing or data format.
   * 9. Updates the loading state, breadcrumbs, session storage, and navigates to the downloads page upon completion.
   *
   * Error handling is performed at multiple stages, and all errors are logged using the error log service.
   *
   * @returns {Subscription} An RxJS subscription to the export process.
   */
  onSubmitGetGoogleEarth(): Subscription {
    let fileName = `810421.logbook_All.kml`;
    this.signalService.isLoading$.set(true);

    return this.fetchMapProj().pipe(
      takeUntil(this.destroy$),
      switchMap((blob) => {
        if (!blob) {
          this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: 
            OnSubmit - Blob is undefined or null`);
          return of();
        }
        // Get the employee number from the signal service
        return this.signalService.employeeNumber$.pipe(
          takeUntil(this.destroy$),
          tap((empNum) => {
            if (!empNum) {
              this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: 
              OnSubmit - Employee number is undefined or null`);
              return;
            }
            
            fileName = `${empNum}.logbook_gx.kml`;
            // Create a new FileReader instance each time
            this.fileReader = new FileReader();

            // Read the blob content and create a KML file
            this.fileReader.onload = (event) => {
              try {
                const jsonData = JSON.parse((event.target?.result ?? '') as string);
                if (jsonData && Array.isArray(jsonData)) {
                  // Generate KML data using the json data from json blob file

                  const kmlData = this.googleEarthService.generateKml(
                    jsonData, 
                    this.gxLabelMarkersToggle, 
                    this.gxLineSelectedColor,
                    this.gxIconMarkersToggle
                  );
                  const kmlBlob: DownloadBlob = {
                    fileName,
                    blobName: new Blob([kmlData], { type: 'application/vnd.google-earth.kml+xml' }),
                    timestamp: DateTime.now().toISO() ?? '',
                  };
                  //save the KML file to the indexedDB
                  this.downloadService.saveFile(fileName, kmlBlob);
                } else {
                  this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: 
                      onSubmitGetGoogleEarth - Invalid JSON data format`);
                }
              } catch (err) {
                this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: 
                    onSubmitGetGoogleEarth - Failed to parse JSON: ${err}`);
              }
            };
            this.fileReader.onloadend = () => {
              this.signalService.isLoading$.set(false);
              this.breadcrumbService.addToBreadcrumbs('/export', 'Export');
              sessionStorage.setItem('export_activeIndex', this.activeIndex?.toString() || '0');
              this.router.navigate(['/export/downloads']);
            }
            
            // Read the blob as text and prcess
            this.fileReader.readAsText(blob.blobName);
          })
        )
      })
    ).subscribe();
  }


  /**
   * Fetches a CSV file as an observable download blob.
   *
   * This method retrieves the current employee number from the signal service,
   * determines the appropriate CSV file type, and attempts to fetch the file.
   * If the file is already available, it checks if an update is needed; otherwise,
   * it downloads the file. Errors encountered during the process are logged and handled.
   *
   * @returns {Observable<DownloadBlob>} An observable that emits the downloaded blob.
   */
  fetchFileCSV(): Observable<DownloadBlob> {
    return this.signalService.employeeNumber$.pipe(
      switchMap((empNum) => {
        if (!empNum) {
          this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: fetchFileCSV -
            Employee number is undefined or null`);
        }
        const fileType = this.getSelectedCsvType(empNum);

        return from(this.downloadService.getFile(fileType.fileName)).pipe(
          switchMap((blob) => {
            if (blob) {
              return this.isUpdateAvailable(
                blob,
                fileType.url,
                empNum,
                fileType.fileName
              );
            }
            return this.downloadService.downloadFile(empNum, fileType.url, fileType.fileName);
          })
        );
      }),
      catchError((err) => {
        this.handleError(err);
        return of();
      })
    );
  }

  /**
   * Fetches the map projection data for the current employee as a downloadable blob.
   *
   * This method retrieves the employee number from the signal service, constructs the appropriate
   * file name and download URL, and attempts to get the file from the local download service.
   * If the file exists, it checks if an update is available; otherwise, it downloads the file from the server.
   * Errors are logged and handled appropriately.
   *
   * @returns {Observable<DownloadBlob>} An observable emitting the downloaded blob or the updated file.
   */
  fetchMapProj(): Observable<DownloadBlob> {
    return this.signalService.employeeNumber$.pipe(
      switchMap((empNum) => {
        if (!empNum) {
          this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: fetchMapProj -
            Employee number is undefined or null`);
        }
        const fileName = `${empNum}.logbook_mp.json`;
        const url = `${this._baseUrl}/Export/json/${empNum}/all`;

        return from(this.downloadService.getFile(fileName)).pipe(
          switchMap((blob) => {
            if (blob) {
              return this.isUpdateAvailable(
                blob,
                url,
                empNum,
                fileName
              )
            }
            return this.downloadService.downloadFile(empNum, url, fileName);
          })
        );
      }),
      catchError((err) => {
        this.handleError(err);
        return of();
      })
    );
  }

  //TODO - DAILY UPDATES NOT IMPLEMENTED FOR PREV DAY YET, CURRENTLY 8 DAYS IN THE REAR
  //Server database updates only occur once a day at specified UTC time, check if passed that daily time
  isUpdateAvailable(blob: DownloadBlob, url: string, empNum: string, fileName: string): Observable<DownloadBlob> {
    const blobTimeStampUTC = DateTime.fromISO(blob.timestamp || '');
    const currentTimeUTC = DateTime.now().setZone('utc');
    const updateTime = 9; // Cosmos update time at 9am Utc
    const isNextDay = currentTimeUTC.diff(blobTimeStampUTC, 'days').days >= 1;

    if (isNextDay && currentTimeUTC.hour >= updateTime) {
      return this.downloadService.downloadFile(empNum, url, fileName);
    }
    else {
      return of(blob);
    }
  }

  /**
   * Generates the appropriate CSV export URL and filename based on the selected export type for a given employee.
   *
   * The export type is determined by the currently selected checkbox, which can represent:
   * - All records
   * - Current month
   * - Current year
   * - Last 6 months
   * - Last 12 months
   * - A custom date range
   * - An effective period
   *
   * The method constructs the URL and filename according to the selected type, using date information
   * retrieved from the download service and form controls.
   *
   * @param empNum - The employee number for whom the CSV export is being generated.
   * @returns An object containing:
   *   - `url`: The API endpoint for downloading the CSV file.
   *   - `fileName`: The suggested filename for the downloaded CSV.
   *
   * If the selection is invalid or required data is missing, returns an object with empty strings.
   */
  getSelectedCsvType(empNum: string): { url: string, fileName: string } {
    const url = `${this._baseUrl}/Export/csv/${empNum}`;
    const currentMonth = this.downloadService.getCurrentMonthDates();
    const currentYear = this.downloadService.getCurrentYearDates();
    const lastSixMonths = this.downloadService.getLastSixMonthsDates();
    const lastTwelveMonths = this.downloadService.getLastTwelveMonthsDates();

    const selected = this.checkboxItems.find(item => item.id === this.selectedCheckboxId) || this.checkboxItems[0];

    if (selected) {
      switch (selected.id) {
        case 0: // All
          return {
            url: `${url}/all`,
            fileName: `${empNum}.logbook_All.csv`
          };
        case 1: // Current Month
          const month = DateTime.fromISO(currentMonth.start).toFormat('MM');
          const monthYear = DateTime.fromISO(currentMonth.start).year;
          return {
            url: `${url}/${currentMonth.start}/${currentMonth.end}`,
            fileName: `${empNum}.logbook_${monthYear}${month}.csv`
          };
        case 2: // Current Year
          const year = DateTime.fromISO(currentMonth.start).year;
          return {
            url: `${url}/${currentYear.start}/${currentYear.end}`,
            fileName: `${empNum}.logbook_${year}.csv`
          };
        case 3: // Last 6 Months
          const lastSixMonthsStart = DateTime.fromISO(lastSixMonths.start).toFormat('MMM');
          const lastSixMonthsEnd = DateTime.fromISO(lastSixMonths.end).toFormat('MMM');
          const lastSixMonthsYear = DateTime.fromISO(lastSixMonths.start).year;
          const lastSixMonthsFileName = `${empNum}.logbook_${lastSixMonthsYear}${lastSixMonthsStart}-${lastSixMonthsEnd}.csv`;
          return {
            url: `${url}/${lastSixMonths.start}/${lastSixMonths.end}`,
            fileName: lastSixMonthsFileName
          };
        case 4: // Last 12 Months
          const lastTwelveMonthsStart = DateTime.fromISO(lastTwelveMonths.start).toFormat('MMM');
          const lastTwelveMonthsEnd = DateTime.fromISO(lastTwelveMonths.end).toFormat('MMM');
          const lastTwelveMonthsYear = DateTime.fromISO(lastTwelveMonths.start).year;
          const lastTwelveMonthsFileName = `${empNum}.logbook_${lastTwelveMonthsYear}${lastTwelveMonthsStart}-${lastTwelveMonthsEnd}.csv`;
          return {
            url: `${url}/${lastTwelveMonths.start}/${lastTwelveMonths.end}`,
            fileName: lastTwelveMonthsFileName
          };
        case 5: // Date Range
          const dateRange = this.downloadService.getDateRanges(this.form.get('dateRange')?.value);
          const dateRangeStart = DateTime.fromISO(dateRange.start).toFormat('yyyyMMdd');
          const dateRangeEnd = DateTime.fromISO(dateRange.end).toFormat('yyyyMMdd');
          if (dateRange.start && dateRange.end) {
            return {
              url: `${url}/${dateRange.start}/${dateRange.end}`,
              fileName: `${empNum}.logbook_${dateRangeStart}-${dateRangeEnd}.csv`
            };
          }
          return { url: url, fileName: '' };
        case 6: // Effective Period
          const effectivePeriod = this.form.get('effectivePeriod')?.value;
          if (effectivePeriod) {
            const ep = DateTime.fromJSDate(effectivePeriod).setZone('utc').toFormat('yyyyMM');
            return {
              url: `${url}/${ep}`,
              fileName: `${empNum}.logbook_EP${ep}.csv`
            };
          }
          return { url: url, fileName: '' };
        default:
          this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: getSelectedCsvType -
            Invalid checkbox selection`);
          return { url: '', fileName: '' };
      }
    }
    return { url: '', fileName: '' };
  }

  calculateTimeLeft(dateTimeString: string): string {
    const targetTime = DateTime.fromISO(dateTimeString).plus({ hours: 1 });
    const now = DateTime.now();
    const diff = targetTime.diff(now, ['hours', 'minutes', 'seconds']);

    if (diff.toMillis() <= 0) {
      return 'Time is up';
    }

    const hours = diff.hours > 0 ? `${Math.floor(diff.hours)}h ` : '';
    const minutes = diff.minutes > 0 ? `${Math.floor(diff.minutes)}m ` : '';
    const seconds = diff.seconds > 0 ? `${Math.floor(diff.seconds)}s` : '';

    return `${hours}${minutes}${seconds}`.trim();
  }

  handleError(err: HttpErrorResponse): void {
    this.signalService.isLoading$.set(false);
    console.error('Error downloading file:', err);
    if (err.status === 429) {
      this.messageService.add({
        severity: 'error',
        summary: 'Request Exceeded',
        detail: `Only 2 requests are allowed per hour. Please try again
            ${this.calculateTimeLeft(this.downloadService.lastDownloadTime || '')} .`,
        life: 10000
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `An error occurred while downloading the file. Please try again later.`,
        life: 10000
      });
    }
  }
  ngOnDestroy(): void {
    if (this.activeIndex !== undefined && this.activeIndex !== null) {
      sessionStorage.setItem('export_activeIndex', this.activeIndex.toString());
    } else {
      sessionStorage.removeItem('export_activeIndex');
    }
    if(this.fileReader) {
      this.fileReader.abort(); // Abort any ongoing file read operation
      this.fileReader = undefined; // Clear the fileReader reference
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  exportAccordion = {
    header: {
      color: '{text.color}',
      padding: '0.75rem 1rem',
      fontWeight: '400'
    },
    content: {
      background: 'transparent',
      padding: '0'
    },
    colorScheme: {
      light: {
        header: {
          background: '{surface.0}',
          hoverBackground: '{surface.0}',
          activeBackground: '{surface.0}',
          activeHoverBackground: '{surface.0}',
        }
      },
      dark: {
        header: {
          background: '{surface.50}',
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.50}',
          activeHoverBackground: '{surface.50}',
        }
      }
    }
  }

  colorPicker = {
    
  }
}
